<script setup>
import { t, message, schemaText } from '../lib/i18n.mjs'
import { computed, reactive, ref } from 'vue'
import ModalShell from './ModalShell.vue'
import AppIcon from './AppIcon.vue'
import UnitRelationsEditor from './UnitRelationsEditor.vue'
import { updateUnitAnnotation } from '../lib/annotations.mjs'
const props = defineProps({ target: Object, document: Object, schemas: Array, links: Array })
const emit = defineEmits(['close', 'save', 'navigate', 'delete'])
const relation = props.target.kind === 'relation'
const item = relation
  ? props.document.edges.find((e) => e.id === props.target.id)
  : props.target.kind === 'unit'
    ? props.document.units.find((u) => u.id === props.target.id)
    : props.document.paragraphs.find((p) => p.id === props.target.id)
const draft = reactive(
  JSON.parse(
    JSON.stringify(
      item || { type: props.links[0].id, level: 'paragraph', source: '', target: '', joint: '', note: '' },
    ),
  ),
)
const unitRelations = ref(
  JSON.parse(
    JSON.stringify(
      props.document.edges.filter(
        (edge) =>
          props.target.kind === 'unit' &&
          edge.level === 'unit' &&
          (edge.source === item.id || edge.target === item.id),
      ),
    ),
  ),
)
if (props.target.kind === 'paragraph') draft.paragraph_representation ??= draft.argument_representation
const paragraph = computed(() =>
  props.target.kind === 'paragraph'
    ? item
    : props.document.paragraphs.find((p) => p.id === item?.paragraphId),
)
const start = ref(item?.sentenceIds?.[0]),
  end = ref(item?.sentenceIds?.at(-1)),
  error = ref('')
const endpoints = computed(() =>
  draft.level === 'paragraph'
    ? props.document.paragraphs.filter((p) => p.kind === 'paragraph')
    : props.document.units,
)
const title = relation
  ? `${item ? '编辑' : '新建'}关系`
  : props.target.kind === 'unit'
    ? '论证单元标注'
    : '段落标注'
function clearEndpoints() {
  draft.source = ''
  draft.target = ''
}
function save() {
  error.value = ''
  if (relation) {
    if (!draft.source || !draft.target || draft.source === draft.target) {
      error.value = '请选择两个不同的端点。'
      return
    }
    if (
      props.document.edges.some(
        (e) =>
          e.id !== draft.id &&
          e.source === draft.source &&
          e.target === draft.target &&
          e.type === draft.type,
      )
    ) {
      error.value = '这条关系已存在。'
      return
    }
  } else if (props.target.kind === 'unit') {
    const a = paragraph.value.sentences.findIndex((s) => s.id === start.value),
      b = paragraph.value.sentences.findIndex((s) => s.id === end.value)
    if (a < 0 || b < a) {
      error.value = '结束句不能在起始句之前。'
      return
    }
    draft.sentenceIds = paragraph.value.sentences.slice(a, b + 1).map((s) => s.id)
  }
  if (props.target.kind === 'unit' && draft.display_label !== item.display_label) draft.summaryManual = true
  const patch = { ...JSON.parse(JSON.stringify(draft)), provenance: 'manual' }
  if (props.target.kind === 'unit') {
    try {
      updateUnitAnnotation(props.document, item.id, patch, unitRelations.value, props.schemas, props.links)
    } catch (cause) {
      error.value = cause.message
      return
    }
    patch.unitRelations = JSON.parse(JSON.stringify(unitRelations.value))
  }
  emit('save', patch)
}
</script>
<template>
  <ModalShell
    :title="t(title)"
    :eyebrow="`${target.id || t('新建')} / ${t({ relation: '关系', unit: '论证单元', paragraph: '段落' }[target.kind])}`"
    :wide="!relation"
    @close="emit('close')"
  >
    <form class="editor-form" @submit.prevent="save">
      <template v-if="relation">
        <label
          >{{ t('层级')
          }}<select v-model="draft.level" :disabled="!!item" @change="clearEndpoints">
            <option value="paragraph">{{ t('段落') }}</option>
            <option value="unit">{{ t('论证单元') }}</option>
          </select></label
        >
        <div class="form-columns">
          <label
            >{{ t('起点')
            }}<select v-model="draft.source" :aria-label="t('起点')" required>
              <option disabled value="">{{ t('选择起点') }}</option>
              <option v-for="node in endpoints" :key="node.id" :value="node.id">
                {{ node.id }} · {{ (node.text || node.sentenceIds.join(', ')).slice(0, 25) }}
              </option>
            </select></label
          ><label
            >{{ t('终点')
            }}<select v-model="draft.target" :aria-label="t('终点')" required>
              <option disabled value="">{{ t('选择终点') }}</option>
              <option v-for="node in endpoints" :key="node.id" :value="node.id">
                {{ node.id }} · {{ (node.text || node.sentenceIds.join(', ')).slice(0, 25) }}
              </option>
            </select></label
          >
        </div>
        <label
          >{{ t('关系类型')
          }}<select v-model="draft.type" :aria-label="t('关系类型')">
            <option v-for="type in links" :key="type.id" :value="type.id">
              {{ schemaText(type, 'name', relation ? 'link' : 'node') }}
            </option>
          </select></label
        >
        <label>{{ t('联合支撑组 ID') }}<input v-model="draft.joint" :placeholder="t('可留空')" /></label>
      </template>
      <template v-else>
        <div class="annotation-context">
          <span class="eyebrow">{{ paragraph.id }} · {{ t('{0} 句', { 0: paragraph.sentences.length }) }}</span>
          <p>
            {{
              target.kind === 'unit'
                ? paragraph.sentences
                    .filter((s) => draft.sentenceIds.includes(s.id))
                    .map((s) => s.text)
                    .join('')
                : paragraph.text
            }}
          </p>
        </div>
        <div class="annotation-summaries">
          <label
            >{{ t('显示摘要')
            }}<input
              v-model="draft.display_label"
              name="display_label"
              maxlength="20"
              :placeholder="t('节点旁显示的简短标签，最多 20 字')"
              :required="item.display_label !== undefined"
          /></label>
          <label v-if="target.kind === 'paragraph'"
            >{{ t('段落摘要（用于分类与关系分析）')
            }}<textarea
              v-model="draft.paragraph_representation"
              name="paragraph_representation"
              rows="2"
              :required="
                item.paragraph_representation !== undefined || item.argument_representation !== undefined
              "
            />
          </label>
          <label v-else-if="item.argument_representation !== undefined"
            >{{ t('论证表示') }}<textarea v-model="draft.argument_representation" rows="2" />
          </label>
        </div>
        <fieldset class="type-choices">
          <legend>{{ t('论证类型') }}</legend>
          <label
            v-for="type in schemas"
            :key="type.id"
            :class="{ active: draft.type === type.id }"
            :style="{ '--type-color': type.color }"
            ><input v-model="draft.type" type="radio" :value="type.id" name="annotation-type" /><i
              :style="{ background: type.color }"
            />{{ schemaText(type, 'name', relation ? 'link' : 'node') }}</label
          >
        </fieldset>
        <div v-if="target.kind === 'unit'" class="form-columns">
          <label
            >{{ t('起始句')
            }}<select v-model="start" :aria-label="t('起始句')">
              <option v-for="sentence in paragraph.sentences" :key="sentence.id" :value="sentence.id">
                {{ sentence.id }} · {{ sentence.text.slice(0, 20) }}
              </option>
            </select></label
          ><label
            >{{ t('结束句')
            }}<select v-model="end" :aria-label="t('结束句')">
              <option v-for="sentence in paragraph.sentences" :key="sentence.id" :value="sentence.id">
                {{ sentence.id }} · {{ sentence.text.slice(0, 20) }}
              </option>
            </select></label
          >
        </div>
        <div v-else class="unit-index">
          <button
            v-for="unit in document.units.filter((u) => u.paragraphId === paragraph.id)"
            :key="unit.id"
            type="button"
            @click="emit('navigate', { kind: 'unit', id: unit.id })"
          >
            <i :style="{ background: schemas.find((s) => s.id === unit.type)?.color }" /><b>{{ unit.id }}</b
            ><span>{{ unit.sentenceIds.join(' – ') }}</span
            ><AppIcon name="ArrowUpRight" :size="14" />
          </button>
        </div>
      </template>
      <UnitRelationsEditor
        v-if="target.kind === 'unit'"
        v-model="unitRelations"
        :unit-id="item.id"
        :document="document"
        :links="links"
      />
      <details
        v-if="item?.provenanceId || item?.summaryProvenance || item?.evidence?.length"
        class="annotation-provenance"
      >
        <summary>{{ t('分析依据与来源') }}</summary>
        <p v-if="item.nuclearity_reason">{{ t('核性：') }}{{ item.nuclearity_reason }}</p>
        <p v-if="item.typeExplanation">{{ t('类型：') }}{{ item.typeExplanation }}</p>
        <template v-if="item.evidence?.length">
          <p>
            {{
              item.evidenceSource === 'paragraph_representation'
                ? t('段落摘要引用（非原文引用）')
                : t('原文引用')
            }}
          </p>
          <p v-for="(citation, index) in item.evidence" :key="index">
            {{ citation.node_id }}：{{ citation.quote }}
          </p>
        </template>
        <p v-else-if="item.evidence_quote">{{ t('原文依据：') }}{{ item.evidence_quote }}</p>
        <p v-if="item.discourse_relation">{{ t('篇章关系：') }}{{ item.discourse_relation }}</p>
        <p v-if="item.schemaVersion">
          {{ t('分类体系版本 {0}', { 0: item.schemaVersion.slice(0, 8) }) }}
        </p>
      </details>
      <label>{{ t('标注备注') }}<textarea v-model="draft.note" rows="3" /></label>
      <p v-if="error" class="form-error" role="alert">{{ message(error) }}</p>
      <footer class="modal-actions">
        <button v-if="relation && item" type="button" class="danger-button" @click="emit('delete', item.id)">
          <AppIcon name="Trash2" :size="14" />{{ t('删除关系') }}</button
        ><span class="action-spacer" /><button type="button" class="secondary-button" @click="emit('close')">
          {{ t('取消') }}</button
        ><button type="submit" class="primary-button">
          <AppIcon name="Check" :size="15" />{{ t('保存标注') }}
        </button>
      </footer>
    </form>
  </ModalShell>
</template>
